import ManagerPlaceActionField from '@/components/common/custom-fields/manager-place-action-field';
import DialogTemplate from '@/components/common/dialog-template/dialog-template';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import UsersListActions from '@/components/users/users-list-actions';
import useGetAvatar from '@/hooks/useGetAvatar';
import { composeUri } from '@/utils/compose';
import {
    AVATARS,
    BIG_SUB_MANAGER_CLIENT_TAG,
    BIG_SUB_MANAGER_SITE_TAG,
    MANAGER_PLACES,
    NAVIGATION_ROUTE,
    USERS,
} from '@/utils/CONST';
import { PersonOutlined } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { find } from 'lodash';
import {
    DataTable,
    DateField,
    FunctionField,
    List,
    ReferenceField,
    TextField,
    TextInput,
    useTranslate,
} from 'react-admin';
import { Outlet, useNavigate, useParams } from 'react-router';

const UsersListDialog = () => {
    const navigate = useNavigate();
    const translate = useTranslate();
    const { clientId, siteId } = useParams();
    const { avatar } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, clientId ?? siteId),
    });

    const handleClose = () => {
        navigate(`/${NAVIGATION_ROUTE}`);
    };

    const isClient = clientId !== undefined;

    return (
        <>
            <DialogTemplate
                maxWidth={'lg'}
                icon={<PersonOutlined />}
                open={true}
                onClose={handleClose}
                title={translate(
                    isClient
                        ? 'main.navigation.users_with_access_to_client'
                        : 'main.navigation.users_with_access_to_site',
                    {
                        name: avatar?.name ?? '',
                    },
                )}
            >
                <ListStylesContainer>
                    <List
                        actions={<UsersListActions clientId={clientId} siteId={siteId} />}
                        component='div'
                        resource={MANAGER_PLACES}
                        filter={{
                            placeOrEvent: clientId ?? siteId,
                        }}
                        filters={[
                            <TextInput
                                source='lastName'
                                label={'main.navigation.last_name'}
                                variant='standard'
                                resettable
                            />,
                            <TextInput
                                source='firstName'
                                label={'main.navigation.name'}
                                variant='standard'
                                resettable
                            />,
                            <TextInput source='email' label='main.navigation.email' variant='standard' resettable />,
                        ]}
                        sort={{ order: 'DESC', field: 'createdAt' }}
                        empty={false}
                        storeKey='usersList'
                        disableSyncWithLocation
                    >
                        <DataTable bulkActionButtons={false}>
                            <DataTable.Col label={'main.navigation.last_name'}>
                                <ReferenceField source='userManager' reference={USERS}>
                                    <TextField source='lastName' />
                                </ReferenceField>
                            </DataTable.Col>

                            <DataTable.Col label={'main.navigation.name'}>
                                <ReferenceField source='userManager' reference={USERS}>
                                    <TextField source='firstName' />
                                </ReferenceField>
                            </DataTable.Col>

                            <DataTable.Col label={'main.navigation.email'}>
                                <ReferenceField source='userManager' reference={USERS}>
                                    <TextField source='email' />
                                </ReferenceField>
                            </DataTable.Col>

                            <DataTable.Col label={'main.navigation.last_login'}>
                                <ReferenceField source='userManager' reference={USERS}>
                                    <DateField
                                        source='lastLogin'
                                        options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                                    />
                                </ReferenceField>
                            </DataTable.Col>

                            <DataTable.Col label={'users.list.user_type'}>
                                <ReferenceField source='userManager' reference={USERS}>
                                    <FunctionField
                                        render={({ tags }: any) => (
                                            <Typography>
                                                {translate(
                                                    `users.tag.${find(tags, (tag) => [BIG_SUB_MANAGER_CLIENT_TAG, BIG_SUB_MANAGER_SITE_TAG].includes(tag))}`,
                                                )}
                                            </Typography>
                                        )}
                                    />
                                </ReferenceField>
                            </DataTable.Col>

                            <DataTable.Col>
                                <FunctionField render={({ id }: any) => <ManagerPlaceActionField itemUriId={id} />} />
                            </DataTable.Col>
                        </DataTable>
                    </List>
                </ListStylesContainer>
            </DialogTemplate>

            <Outlet />
        </>
    );
};

export default UsersListDialog;
